import React, { useState, useEffect } from 'react';

const SubscriptionsHero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [particles, setParticles] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    setIsVisible(true);

    // Generate more dynamic particles
    const newParticles = Array.from({ length: 80 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 2,
      speed: Math.random() * 3 + 1,
      opacity: Math.random() * 0.7 + 0.3,
      color: Math.random() > 0.5 ? 'blue' : 'purple',
    }));
    setParticles(newParticles);

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const handleNavClick = (href) => {
    // In a real application, this would use React Router or Next.js router
    window.location.href = href;
  };

  return (
    <div
      className="relative min-h-screen bg-white overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Dynamic Background Mesh */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 60%, rgba(14, 165, 233, 0.08) 0%, transparent 50%)
            `,
            animation: 'meshMove 15s ease-in-out infinite',
          }}
        />
      </div>

      {/* Animated Grid */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, #3b82f6 1px, transparent 1px),
              linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            animation: 'gridPulse 8s ease-in-out infinite',
            transform: `translateY(${scrollY * 0.1}px)`,
          }}
        />
      </div>

      {/* Enhanced Floating Particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className={`absolute rounded-full ${
            particle.color === 'blue' ? 'bg-blue-400' : 'bg-purple-400'
          } shadow-lg`}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            opacity: particle.opacity,
            animation: `particleFloat ${particle.speed * 4 + 6}s ease-in-out infinite`,
            animationDelay: `${particle.id * 0.05}s`,
            pointerEvents: 'none',
            boxShadow: `0 0 ${particle.size * 2}px ${
              particle.color === 'blue' ? 'rgba(59, 130, 246, 0.5)' : 'rgba(168, 85, 247, 0.5)'
            }`,
          }}
        />
      ))}

      {/* Interactive Gradient Orb */}
      <div
        className="absolute inset-0 pointer-events-none transition-all duration-300"
        style={{
          background: `radial-gradient(800px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(59, 130, 246, 0.08), rgba(168, 85, 247, 0.05) 40%, transparent 70%)`,
        }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-start px-6 pt-24 sm:pt-28 lg:pt-32 pb-20">
        <div className="max-w-7xl mx-auto text-center">
          {/* Animated Badge */}
          <div
            className={`inline-flex items-center gap-3 px-6 py-3 rounded-full border border-blue-400/30 bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm mb-6 transform transition-all duration-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 animate-pulse shadow-lg" />
            <span className="text-sm font-semibold text-blue-700 tracking-wide uppercase">
              Quantum-AI Security Revolution
            </span>
          </div>

          {/* Main Heading - Tightly Spaced */}
          <div
            className={`mb-6 transform transition-all duration-1000 delay-200 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black leading-none tracking-tight">
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent animate-pulse">
                WELCOME TO
              </span>
              <span className="block text-gray-900 relative mt-2 md:mt-3">
                GQBS LAB
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-xl opacity-20 animate-pulse" />
              </span>
            </h1>
          </div>

          {/* Subtitle - Compact */}
          <p
            className={`text-lg md:text-xl lg:text-2xl text-gray-700 mb-8 max-w-4xl mx-auto leading-tight font-light transform transition-all duration-1000 delay-400 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            Where{' '}
            <span className="text-blue-400 font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              innovation meets intelligence
            </span>
            . Subscribe once, access everything:{' '}
            <span className="text-purple-400 font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              AI, Quantum, Cybersecurity — unified
            </span>
          </p>

          {/* Innovation Hub Overview - Moved Up */}
          <div
            className={`max-w-6xl mx-auto mb-10 transform transition-all duration-1000 delay-600 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              GLOCYBS Innovation Hub Overview
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { 
                  name: 'BIQUA', 
                  desc: 'AI-Quantum Cybersecurity Defender', 
                  color: 'blue',
                  link: '/products/biqua'
                },
                { 
                  name: 'QUBISA', 
                  desc: 'Quantum-AI Intelligence Assistant', 
                  color: 'purple',
                  link: '/products/qubisa'
                },
                { 
                  name: 'G-FLEA', 
                  desc: 'Ultra-fast Integration Engine', 
                  color: 'cyan',
                  link: '/products/g-flea'
                },
              ].map((product, index) => (
                <button
                  key={product.name}
                  onClick={() => handleNavClick(product.link)}
                  className={`p-6 rounded-2xl bg-gradient-to-br ${
                    product.color === 'blue' ? 'from-blue-50 to-cyan-50 border-blue-300' :
                    product.color === 'purple' ? 'from-purple-50 to-pink-50 border-purple-300' :
                    'from-cyan-50 to-blue-50 border-cyan-300'
                  } border hover:scale-105 transition-all duration-300 cursor-pointer group shadow-lg hover:shadow-xl text-left w-full`}
                >
                  <h3 className={`text-xl font-black mb-3 bg-gradient-to-r ${
                    product.color === 'blue' ? 'from-blue-400 to-cyan-400' :
                    product.color === 'purple' ? 'from-purple-400 to-pink-400' :
                    'from-cyan-400 to-blue-400'
                  } bg-clip-text text-transparent group-hover:scale-110 transition-transform`}>
                    {product.name}
                  </h3>
                  <p className="text-gray-700 font-medium mb-2">{product.desc}</p>
                  <div className="flex items-center text-blue-600 text-sm font-semibold group-hover:text-purple-600 transition-colors">
                    Explore →
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Feature Pills - Compact */}
          <div
            className={`flex flex-wrap justify-center gap-3 mb-8 transform transition-all duration-1000 delay-800 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            {[
              'Quantum-AI Security',
              'Unified Platform',
              'Next-Gen Engine',
              'For Pioneers',
            ].map((feature, index) => (
              <div
                key={feature}
                className="px-5 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-300 text-blue-700 font-semibold hover:scale-110 hover:bg-gradient-to-r hover:from-blue-200 hover:to-purple-200 transition-all duration-300 cursor-pointer"
                style={{ animationDelay: `${1000 + index * 100}ms` }}
              >
                {feature}
              </div>
            ))}
          </div>

          {/* CTA Buttons - Enhanced */}
          <div
            className={`flex flex-col sm:flex-row gap-6 justify-center items-center mb-10 transform transition-all duration-1000 delay-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <button 
              onClick={() => handleNavClick('/subscriptions/start')}
              className="group relative px-10 py-5 bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 text-white rounded-full font-bold text-lg hover:scale-110 transform transition-all duration-300 shadow-2xl hover:shadow-blue-500/50 overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                Start Quantum Journey
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>

            <button 
              onClick={() => handleNavClick('/demo')}
              className="group px-10 py-5 border-2 border-blue-600 text-blue-600 rounded-full font-bold text-lg hover:bg-blue-600 hover:text-white transform hover:scale-110 transition-all duration-300 relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                See GQBS Live
                <svg className="w-5 h-5 group-hover:rotate-90 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              </span>
              <div className="absolute inset-0 bg-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </button>
          </div>

          {/* Stats - Enhanced */}
          <div
            className={`grid grid-cols-3 gap-8 max-w-3xl mx-auto transform transition-all duration-1000 delay-1200 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            {[
              { number: '100K+', label: 'Global Innovators', color: 'blue' },
              { number: '$10M+', label: 'Annual Savings', color: 'purple' },
              { number: '99.99%', label: 'Uptime', color: 'cyan' },
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center group cursor-pointer"
              >
                <div className={`text-2xl md:text-3xl font-black mb-2 group-hover:scale-125 transition-all duration-300 bg-gradient-to-r ${
                  stat.color === 'blue' ? 'from-blue-400 to-cyan-400' :
                  stat.color === 'purple' ? 'from-purple-400 to-pink-400' :
                  'from-cyan-400 to-blue-400'
                } bg-clip-text text-transparent`}>
                  {stat.number}
                </div>
                <div className="text-gray-600 text-sm font-semibold uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Animated Tech Elements */}
      <div
        className="absolute top-20 left-10 w-24 h-24 border-2 border-blue-300 rounded-2xl transform rotate-45 animate-spin opacity-20 shadow-lg shadow-blue-500/10"
        style={{ animationDuration: '15s' }}
      />
      <div className="absolute bottom-20 right-10 w-20 h-20 border-2 border-purple-300 rounded-full animate-pulse opacity-30 shadow-lg shadow-purple-500/10" />
      <div
        className="absolute top-1/2 left-5 w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full opacity-20 animate-bounce shadow-lg shadow-cyan-500/20"
        style={{ animationDelay: '1s' }}
      />
      <div
        className="absolute top-1/3 right-20 w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg opacity-15 animate-pulse shadow-lg shadow-purple-500/10"
        style={{ animationDelay: '2s' }}
      />

      <style jsx>{`
        @keyframes meshMove {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          33% {
            transform: translate(50px, -30px) rotate(120deg);
          }
          66% {
            transform: translate(-30px, 50px) rotate(240deg);
          }
        }

        @keyframes gridPulse {
          0%, 100% {
            opacity: 0.1;
            transform: scale(1);
          }
          50% {
            opacity: 0.2;
            transform: scale(1.05);
          }
        }

        @keyframes particleFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
          }
          25% {
            transform: translateY(-30px) rotate(90deg) scale(1.1);
          }
          50% {
            transform: translateY(-15px) rotate(180deg) scale(0.9);
          }
          75% {
            transform: translateY(-25px) rotate(270deg) scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default SubscriptionsHero;